import React, { useEffect, useRef, useState } from "react"
import { useTabs }    from "../context/TabsContext"
import { usePanorama } from "../context/PanoramaContext"
import StatusChip     from "./StatusChip"

const CondensedIdentity = ({ project }) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <span className="text-[13px] font-semibold text-slate-900 truncate max-w-[22ch]" title={project.name}>
      {project.name}
    </span>
    <StatusChip status={project.status} label={project.status_label} />
  </div>
)

const PILL_ACTIVE   = "bg-anthracite text-white"
const PILL_IDLE     = "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
const PILL_MUTED    = "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
const PILL_SHAPE    = "shrink-0 inline-flex items-center px-3.5 py-1.5 rounded-full text-[13px] transition-colors border-0 shadow-none focus:outline-none focus:shadow-none focus-visible:outline-none"

const Count = ({ value, active }) => {
  if (value == null) return null
  return (
    <span
      className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold tabular-nums ${
        value === 0
          ? "bg-slate-100 text-slate-400"
          : active
            ? "bg-white/20 text-white"
            : "bg-slate-100 text-slate-600"
      }`}
    >
      {value}
    </span>
  )
}

const TabNav = () => {
  const { families, family, current, setCurrent } = useTabs()
  const { state } = usePanorama()
  const project = state.project

  const sentinel = useRef(null)
  const [condensed, setCondensed] = useState(false)

  const barRef     = useRef(null)
  const familyRefs = useRef({})
  const [subOffset, setSubOffset] = useState(0)

  useEffect(() => {
    const align = () => {
      const bar    = barRef.current
      const button = familyRefs.current[family?.id]
      if (!bar || !button) return
      setSubOffset(Math.max(0, button.getBoundingClientRect().left - bar.getBoundingClientRect().left))
    }
    align()
    window.addEventListener("resize", align)
    return () => window.removeEventListener("resize", align)
  }, [family?.id, families])

  useEffect(() => {
    const node = sentinel.current
    if (!node || typeof IntersectionObserver === "undefined") return

    const observer = new IntersectionObserver(
      ([entry]) => setCondensed(!entry.isIntersecting),
      { rootMargin: "-56px 0px 0px 0px", threshold: 0 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <div ref={sentinel} aria-hidden="true" />

      <div ref={barRef} className="panorama-tabnav sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="pl-[44px] pr-7 py-2 flex items-center gap-4 border-b border-slate-100">
          <nav className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {families.map(item => {
              const active = item.id === family?.id
              return (
                <button
                  key={item.id}
                  ref={node => { familyRefs.current[item.id] = node }}
                  type="button"
                  onClick={() => setCurrent(item.sections[0].id)}
                  className={`${PILL_SHAPE} ${active ? `${PILL_ACTIVE} font-semibold` : `${PILL_IDLE} font-medium`}`}
                >
                  {item.label}
                </button>
              )
            })}
          </nav>

          <div
            className={`ml-auto flex items-center gap-3 shrink-0 transition-opacity duration-150 ${
              condensed ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <CondensedIdentity project={project} />
          </div>
        </div>

        <div
          className="pr-7 py-2 flex gap-1.5 overflow-x-auto no-scrollbar transition-[padding] duration-200"
          style={{ paddingLeft: subOffset || 44 }}
        >
          {(family?.sections || []).map(section => {
            const active = section.id === current
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setCurrent(section.id)}
                className={`${PILL_SHAPE} ${
                  active
                    ? `${PILL_ACTIVE} font-semibold`
                    : section.count === 0
                      ? `${PILL_MUTED} font-medium`
                      : `${PILL_IDLE} font-medium`
                }`}
              >
                {section.label}
                <Count value={section.count} active={active} />
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default TabNav
